import * as React from 'react';
import { css } from 'aphrodite';
import * as Api from '@ViewModels';
import { AppDrawerContent } from '../../../../web/components-v2/Drawer/AppDrawer';
import {
	useAutomationTemplateEligibleContactsQuery,
	useAutomationTemplateContactStepsQuery,
} from '../../../../queries/Automation';
import { useContactSearchQuery } from '../../../../queries/Contact/useContactSearchQuery';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { TextInput } from '../../../../web/components/TextInput';
import { TestContact } from '../types';
import { styleSheet } from '../styles';
import { ContactCard } from '../../../../shared/components/automation/ContactCard';
import { ContactProgressIndicator } from './ContactProgressIndicator';
import { CONTACT_COLORS } from '../../../../web/containers/AutomationFlows/types/contacts';

interface TestingContactsDrawerContentProps {
	templateId: string;
	selectedContacts: TestContact[];
	onContactsChange: (contacts: TestContact[]) => void;
	impersonationContext?: Api.IImpersonationContext;
	useDraft: boolean;
}

const MAX_CONTACTS = 5;

export const TestingContactsDrawerContent: React.FC<TestingContactsDrawerContentProps> = ({
	templateId,
	selectedContacts,
	onContactsChange,
	impersonationContext,
	useDraft,
}) => {
	const { data: eligibleContactsData, isLoading: isLoadingEligible } = useAutomationTemplateEligibleContactsQuery({
		templateId,
		limit: MAX_CONTACTS,
		impersonationContext,
	});

	const eligibleContacts = eligibleContactsData?.values || [];
	const maxReached = selectedContacts.length >= MAX_CONTACTS;
	const selectedContactIds = new Set(selectedContacts.map(tc => tc.contact.id));
	const [searchInput, setSearchInput] = React.useState('');
	const debouncedSearch = useDebounceValue(searchInput.trim(), 400);
	const shouldSearch = debouncedSearch.length >= 2 && !maxReached;
	const {
		data: contactSearchData,
		isFetching: isSearchingContacts,
		isError: isSearchError,
	} = useContactSearchQuery({
		searchQuery: debouncedSearch,
		pageSize: 5,
		enabled: shouldSearch,
		impersonationContext,
	});
	const searchResults = contactSearchData?.values || [];
	const filteredSearchResults = searchResults.filter((contact: Api.IContact) => !selectedContactIds.has(contact.id));

	const handleAddContact = (contact: Api.IContact) => {
		if (maxReached || selectedContactIds.has(contact.id)) {
			return;
		}

		const color = CONTACT_COLORS[selectedContacts.length % CONTACT_COLORS.length];
		const newTestContact: TestContact = {
			contact,
			color,
			isSelected: true,
			steps: undefined,
		};

		onContactsChange([...selectedContacts, newTestContact]);
	};

	const handleToggleSelect = (contactId: string) => {
		const existingIndex = selectedContacts.findIndex(tc => tc.contact.id === contactId);

		if (existingIndex >= 0) {
			const updated = selectedContacts.filter((_, index) => index !== existingIndex);
			updated.forEach((tc, index) => {
				tc.color = CONTACT_COLORS[index % CONTACT_COLORS.length];
			});
			onContactsChange(updated);
		}
	};

	const handleClearAll = () => {
		onContactsChange([]);
	};

	return (
		<AppDrawerContent value='testing-contacts'>
			<div className={css(styleSheet.drawerContent)}>
				<section className={css(styleSheet.section)}>
					<h3 className={css(styleSheet.sectionHeader)}>Search Contacts</h3>
					<p className={css(styleSheet.searchDescription)}>
						Find contacts by name or email. Results respect the current impersonated account so you can test with real
						data.
					</p>
					<div className={css(styleSheet.searchInputRow)}>
						<TextInput
							type='text'
							inputId='automation-testing-contact-search'
							value={searchInput}
							onChange={e => setSearchInput(e.target.value)}
							placeholder={maxReached ? 'Contact limit reached' : 'Search by name or email'}
							disabled={maxReached}
						/>
						{searchInput ? (
							<button type='button' className={css(styleSheet.searchClearButton)} onClick={() => setSearchInput('')}>
								Clear
							</button>
						) : null}
					</div>
					{maxReached ? (
						<p className={css(styleSheet.emptyState)}>Remove a selected contact to add another.</p>
					) : !shouldSearch ? (
						<div className={css(styleSheet.searchHint)}>Enter at least two characters to search.</div>
					) : isSearchingContacts ? (
						<p className={css(styleSheet.emptyState)}>Searching contacts...</p>
					) : isSearchError ? (
						<p className={css(styleSheet.emptyState)}>Unable to search contacts. Please try again.</p>
					) : filteredSearchResults.length === 0 ? (
						<p className={css(styleSheet.emptyState)}>No contacts found.</p>
					) : (
						<ul className={css(styleSheet.searchResults)}>
							{filteredSearchResults.map((contact: Api.IContact) => (
								<li key={contact.id} className={css(styleSheet.searchResultRow)}>
									<div className={css(styleSheet.searchResultInfo)}>
										<div className={css(styleSheet.eligibleContactName)}>
											{contact.firstName} {contact.lastName}
										</div>
										{contact.primaryEmail?.value ? (
											<div className={css(styleSheet.eligibleContactEmail)}>{contact.primaryEmail.value}</div>
										) : null}
									</div>
									<button
										type='button'
										onClick={() => handleAddContact(contact)}
										className={css(styleSheet.addContactButton)}
									>
										Add
									</button>
								</li>
							))}
						</ul>
					)}
				</section>

				<section className={css(styleSheet.section)}>
					<div className={css(styleSheet.sectionHeader)}>
						Eligible Contacts {eligibleContacts.length > 0 && `(${eligibleContacts.length})`}
					</div>
					{isLoadingEligible ? (
						<div className={css(styleSheet.emptyState)}>Loading eligible contacts...</div>
					) : eligibleContacts.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>No eligible contacts found</div>
					) : (
						<div>
							{eligibleContacts.slice(0, MAX_CONTACTS - selectedContacts.length).map((contact: Api.IContact) => {
								const isAlreadySelected = selectedContactIds.has(contact.id);
								if (isAlreadySelected) {
									return null;
								}

								return (
									<div
										key={contact.id}
										onClick={() => handleAddContact(contact)}
										className={css(
											styleSheet.eligibleContactCard,
											maxReached && styleSheet.eligibleContactCardDisabled
										)}
									>
										<div className={css(styleSheet.eligibleContactName)}>
											{contact.firstName} {contact.lastName}
										</div>
										{contact.primaryEmail && (
											<div className={css(styleSheet.eligibleContactEmail)}>{contact.primaryEmail.value}</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</section>

				<section className={css(styleSheet.section)}>
					<div className={css(styleSheet.selectedContactsHeader)}>
						<div className={css(styleSheet.sectionHeader, styleSheet.sectionHeaderNoMargin)}>
							Selected Contacts ({selectedContacts.length}/{MAX_CONTACTS})
						</div>
						{selectedContacts.length > 0 && (
							<button type='button' onClick={handleClearAll} className={css(styleSheet.clearAllButton)}>
								Clear All
							</button>
						)}
					</div>

					{selectedContacts.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>No contacts selected</div>
					) : (
						<div>
							{selectedContacts.map(testContact => (
								<ContactCardWithSteps
									key={testContact.contact.id}
									testContact={testContact}
									templateId={templateId}
									onToggleSelect={handleToggleSelect}
									impersonationContext={impersonationContext}
									useDraft={useDraft}
									selectedContacts={selectedContacts}
									onContactsChange={onContactsChange}
								/>
							))}
						</div>
					)}

					{maxReached && <div className={css(styleSheet.contactLimit)}>Maximum of {MAX_CONTACTS} contacts reached</div>}
				</section>
			</div>
		</AppDrawerContent>
	);
};

const ContactCardWithSteps: React.FC<{
	testContact: TestContact;
	templateId: string;
	onToggleSelect: (contactId: string) => void;
	impersonationContext?: Api.IImpersonationContext;
	useDraft: boolean;
	selectedContacts: TestContact[];
	onContactsChange: (contacts: TestContact[]) => void;
}> = ({
	testContact,
	templateId,
	onToggleSelect,
	impersonationContext,
	useDraft,
	selectedContacts,
	onContactsChange,
}) => {
	const { data: steps } = useAutomationTemplateContactStepsQuery({
		templateId,
		contactId: testContact.contact.id,
		useDraft,
		impersonationContext,
	});

	// Update parent state when steps are loaded
	React.useEffect(() => {
		if (steps && steps !== testContact.steps) {
			// Find the contact in the selectedContacts array
			const updatedContacts = selectedContacts.map(tc =>
				tc.contact.id === testContact.contact.id ? { ...tc, steps } : tc
			);
			onContactsChange(updatedContacts);
		}
	}, [steps, testContact.contact.id, testContact.steps, selectedContacts, onContactsChange]);

	const contactWithSteps: TestContact = {
		...testContact,
		steps: steps || testContact.steps,
	};

	const filterKeyFacts = (fact: string) => /insurance|policy|renewal/i.test(fact);

	return (
		<ContactCard
			contact={contactWithSteps.contact}
			onToggle={onToggleSelect}
			isSelected={contactWithSteps.isSelected}
			color={contactWithSteps.color}
			showEmail={true}
			showPhone={true}
			filterKeyFacts={filterKeyFacts}
			renderAdditionalContent={() => {
				if (contactWithSteps.isSelected && contactWithSteps.steps && contactWithSteps.steps.length > 0) {
					return <ContactProgressIndicator steps={contactWithSteps.steps} />;
				}
				return null;
			}}
		/>
	);
};
